import React, { Dispatch, FC, Fragment, useMemo, useState } from 'react';
import { Box, Checkbox, TableBody, TableCell, TableRow, Collapse } from '@mui/material';
import { classes } from './styles';
import { handleSelectOne } from './tableHelper';
import {
  GenericTableData,
  TSortableColumn,
  TTableState,
  TableActionItemAction,
  TableSelectionAction
} from './types';
import GenericTableActions, { ActionListItem } from './GenericTableActions';

interface IGenericTableBodyProps<T> {
  tableState: TTableState<T, TSortableColumn[]>;
  dispatch: Dispatch<TableActionItemAction<T> | TableSelectionAction<T>>;
  items: T[];
  TableRows: FC<{
    column: TSortableColumn;
    item: T;
    tableHovered: boolean;
    dispatch?: Dispatch<TableActionItemAction<T> | TableSelectionAction<T>>;
    onExpand?: () => void;
  }>;
  TableActionList?: ActionListItem[];
  allowMultipleSelection?: boolean;
  ExpandedContent?: FC<{ item: T }>;
  expandableCellIds?: string[];
}

const GenericTableBody = <T extends GenericTableData>({
  tableState,
  dispatch,
  items,
  TableRows,
  TableActionList,
  allowMultipleSelection = true,
  ExpandedContent,
  expandableCellIds = []
}: IGenericTableBodyProps<T>) => {
  const allItemIds = useMemo(() => items.map((item) => item.id), [items]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleRowExpand = (itemId: number) => {
    setExpandedRows((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  return (
    <TableBody>
      {items.map((item) => (
        <Fragment key={item.id}>
          <TableRow sx={{ ...(ExpandedContent && { borderBottom: 'none !important' }) }}>
            {allowMultipleSelection && (
              <TableCell sx={{ ...classes.sticky, width: '58px' }}>
                <Checkbox
                  id={`select-checkbox-${item.id}`}
                  sx={classes.checkbox}
                  checked={tableState.selectedItemIds.includes(item.id)}
                  onChange={(event) => handleSelectOne(event, item, allItemIds, dispatch)}
                  inputProps={{
                    'aria-label': `select ${item.name}`
                  }}
                />
              </TableCell>
            )}
            {tableState.columns.map(
              (column) =>
                column.enabled && (
                  <TableRows
                    key={column.id}
                    item={item}
                    column={column}
                    tableHovered={tableState.tableHovered}
                    dispatch={dispatch}
                    onExpand={
                      expandableCellIds.includes(column.id)
                        ? () => handleRowExpand(item.id)
                        : undefined
                    }
                  />
                )
            )}
            <TableCell
              sx={{
                zIndex: '4',
                ...classes.actionsTableCell,
                ...classes.stickyRight,
                ...(tableState.tableHovered && classes.scrollShadowVisible),
                ...(!TableActionList && { minWidth: '24px', width: '24px' })
              }}
            >
              <Box sx={classes.actionItems}>
                {TableActionList && (
                  <GenericTableActions item={item} dispatch={dispatch} actions={TableActionList} />
                )}
              </Box>
            </TableCell>
          </TableRow>
          {ExpandedContent && (
            <TableRow>
              <TableCell
                style={{ paddingBottom: 0, paddingTop: 0 }}
                colSpan={tableState.columns.length + (allowMultipleSelection ? 2 : 1)}
              >
                <Collapse in={expandedRows.includes(item.id)} timeout="auto" unmountOnExit>
                  <Box margin={1}>
                    <ExpandedContent item={item} />
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      ))}
    </TableBody>
  );
};

export default GenericTableBody;
